import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import { Divider } from "@ps-design-system/components/Divider";
import { Form } from "@ps-design-system/components/Form";
import { Text } from "@ps-design-system/components/Text";

import type { Listing } from "../../../../../types/listings.types";
import { useListingFsvForm } from "../useListingFsvForm";

import { FoodAndDrinkMinimumField } from "./form-fields/FoodAndDrinkMinimumField";
import { GratuityRateField } from "./form-fields/GratuityRateField";
import { MenuField } from "./form-fields/MenuField";
import { RoomTypeField } from "./form-fields/RoomTypeField";
import { SpaceRentalField } from "./form-fields/SpaceRentalField";

type ListingFsvFormProps = {
  listing: Listing;
};

const ListingFsvForm = ({ listing }: ListingFsvFormProps) => {
  const {
    currencySymbol,
    errorMsg,
    form,
    isEditingPricing,
    isSaving,
    isValidatingPricing,
    onSave,
    roundUpAmounts,
    startEditingPricing,
    validatePricing,
  } = useListingFsvForm(listing);

  // Round fractions up first so the pricing check sees whole numbers
  const handlePricingBlur = () => {
    roundUpAmounts();
    validatePricing();
  };

  const handleSave = async () => {
    const success = await onSave();
    if (success) {
      window.location.href = `/mylistings/${listing._id}`;
    }
  };

  return (
    <Form {...form}>
      {/* Locked while saving, so pricing can't change under the check and PUT */}
      <fieldset
        disabled={isSaving}
        className="tw-m-0 tw-min-w-0 tw-border-0 tw-p-0"
      >
        <RoomTypeField />
        <FoodAndDrinkMinimumField
          currencySymbol={currencySymbol}
          onBlur={handlePricingBlur}
          onFocus={startEditingPricing}
        />
        <SpaceRentalField
          currencySymbol={currencySymbol}
          onBlur={handlePricingBlur}
          onFocus={startEditingPricing}
        />
        <GratuityRateField onBlur={roundUpAmounts} />
        <MenuField />
      </fieldset>

      <Divider />

      <div className="tw-flex tw-mt-32 tw-justify-end">
        <div className="tw-flex tw-flex-col tw-self-end tw-gap-8">
          <Button
            appearance="purple"
            disabled={
              !form.formState.isDirty ||
              isEditingPricing ||
              isValidatingPricing ||
              isSaving
            }
            onClick={handleSave}
            showSpinner={isSaving || isValidatingPricing}
            testId="fsv-form-update-button"
          >
            <FormattedMessage
              id="hosting.listing_fsv.save_button"
              defaultMessage="Save"
            />
          </Button>
          {errorMsg && (
            <Text
              typeface="body-1"
              element="p"
              className="tw-text-red-800"
              testId="fsv-form-submit-error"
            >
              {errorMsg}
            </Text>
          )}
        </div>
      </div>
    </Form>
  );
};

export { ListingFsvForm };
